import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Collection } from 'firestorter';
import moment from 'moment';
import * as Sentry from '@sentry/browser';

import ChatMessage from '../../../chat/Model/ChatMessage';
import ChatChannel from '../../../chat/Model/ChatChannel';
import CollectionName from '../../../chat/utils/collections';
import User from '../../../Model/User';
import { DateFormat } from '../../../utils/date';
import { downloadCsv } from '../../utils/csv';

import GenericDataGrid from '../../components/GenericDataGrid';
import LoadingOverlay from '../../components/LoadingOverlay';
import CoachSelect from '../../components/Leads/components/CoachSelect';
import UserSelect from '../Analytics/components/UserSelect';
import Select from '../../../components/Select';
import DownloadCSVButton from '../../components/DownloadCSVButton';

import {
  StyledHeader,
  Title,
  OptionContainer,
  SelectWrapper,
  DataGridContainer,
  AddedText,
  DeletedText,
  ScrollableCell,
  ScrollableCellContent,
  MedianContainer,
  MedianInfoWrapper,
  MedianLabel,
  MedianValue,
} from './styles';
import texts from './texts.json';
import {
  DEFAULT_ROW_HEIGHT,
  MESSAGES_PER_USER_OPTIONS,
  DEFAULT_MESSAGES_PER_USER_SELECTED,
  CSV_HEADERS,
} from './constants';
import {
  buildLangfuseUrl,
  tokenize,
  buildLCSMatrix,
  diffTokens,
} from './utils';

/**
 * Compares two messages and returns:
 * - JSX-renderable diff (only highlights the modified message)
 * - Percentage of meaningful text that changed
 */
const useMessageDiff = () => useCallback((originalText, modifiedText) => {
  if (!modifiedText) {
    return { diff: null, percentChanged: 0 };
  }

  if (!originalText) {
    return {
      diff: <AddedText>{modifiedText}</AddedText>,
      percentChanged: 100,
    };
  }

  if (originalText === modifiedText) {
    return {
      diff: modifiedText,
      percentChanged: 0,
    };
  }

  const originalTokens = tokenize(originalText);
  const modifiedTokens = tokenize(modifiedText);

  const dp = buildLCSMatrix(originalTokens, modifiedTokens);
  const diff = diffTokens(originalTokens, modifiedTokens, dp);

  // ---------- % CHANGE CALCULATION ----------
  let sameCount = 0;
  let meaningfulCount = 0;

  diff.forEach((part) => {
    // Ignore pure whitespace when calculating change
    if (/^\s+$/.test(part.value)) return;

    meaningfulCount += 1;

    if (part.type === 'same') {
      sameCount += 1;
    }
  });

  const percentChanged = meaningfulCount === 0
    ? 0
    : Math.round((1 - sameCount / meaningfulCount) * 100);

  // ---------- RENDER JSX ----------
  const renderedDiff = diff.map((part, index) => {
    // Skip rendering deleted whitespace
    if (part.type === 'deleted' && /^\s+$/.test(part.value)) {
      return null;
    }

    const key = `${part.type}-${index}-${part.value.slice(0, 10)}`;

    switch (part.type) {
      case 'added':
        return /^\s+$/.test(part.value) ? (
          <span key={key}>{part.value}</span>
        ) : (
          <AddedText key={key}>{part.value}</AddedText>
        );

      case 'deleted':
        return <DeletedText key={key}>{part.value}</DeletedText>;

      default:
        return <span key={key}>{part.value}</span>;
    }
  });

  return {
    diff: renderedDiff,
    percentChanged,
  };
}, []);

const SmartContextAnalytics = () => {
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [selectedMessagesPerUser, setSelectedMessagesPerUser] = useState(DEFAULT_MESSAGES_PER_USER_SELECTED);

  const coachId = selectedCoach?.id;
  const userId = selectedUser?.id;

  useEffect(() => {
    const loadUsers = async () => {
      if (!coachId) {
        setUsers([]);
        return;
      }

      setIsLoading(true);
      try {
        const userCollection = await User.getUsersByCoach(coachId);

        const transformedUsers = userCollection.docs.map((doc) => {
          if (doc) {
            return {
              userId: doc.id,
              userName: doc.name,
            };
          }
          return null;
        }).filter(Boolean)
          .sort((a, b) => a.userName.localeCompare(b.userName));
        setUsers(transformedUsers);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: 'SmartContextAnalytics',
            action: 'loadUsers',
          },
          extra: {
            coachId,
          },
        });
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [coachId]);

  const onCoachSelect = useCallback((coach) => {
    setSelectedCoach(coach || null);
    setSelectedUser(null);
  }, []);

  const onUserSelect = useCallback((user) => {
    setSelectedUser(user);
  }, []);

  const messageDiff = useMessageDiff();

  /**
   * Processes a message document and returns a formatted message object for analytics
   */
  const processMessageDoc = useCallback((doc) => {
    const augmentedText = doc.data?.augmentedText || '';
    const text = doc.text || '';

    // If no augmentedText exists (no edit was made), use text as augmentedText (0% change)
    const normalizedAugmentedText = (!augmentedText || augmentedText.trim() === '') ? text : augmentedText;

    const createdAt = doc.createdAt || null;
    const smartContextTraceId = doc.data?.smartContextTraceId || '';
    const langfuseUrl = buildLangfuseUrl(smartContextTraceId);

    // Skip diff calculation if texts are the same
    let diff;
    let percentChangedValue = 0;
    if (normalizedAugmentedText === text) {
      diff = text;
    } else {
      const { diff: calculatedDiff, percentChanged } = messageDiff(normalizedAugmentedText, text);
      diff = calculatedDiff;
      percentChangedValue = percentChanged ?? 0;
    }

    return {
      id: doc.id,
      diff,
      percentChanged: percentChangedValue,
      [texts.columns.aiAugmentedText]: normalizedAugmentedText || '',
      [texts.columns.sentMessages]: text || '',
      [texts.columns.percentChanged]: `${percentChangedValue}%`,
      [texts.columns.date]: createdAt
        ? moment(createdAt).format(DateFormat.DATE_FORMAT_COMMA)
        : '-',
      [texts.columns.langfuseUrl]: langfuseUrl,
    };
  }, [messageDiff]);

  // Fetch chat messages based on user/coach selection
  useEffect(() => {
    const loadChatMessages = async () => {
      setIsLoadingMessages(true);
      try {
        // No user and no coach selected -> no messages
        if (!userId && !coachId) {
          setChatMessages([]);
          return;
        }

        // Only user selected, no coach -> display all user's augmented messages
        if (userId && !coachId) {
          const channelsCollection = new Collection(CollectionName.CHAT_CHANNEL, {
            createDocument: (src, opts) => new ChatChannel(src.id, opts),
            query: (ref) => ref
              .where('userId', '==', userId),
          });

          await channelsCollection.fetch();
          const channels = channelsCollection.docs;

          if (channels.length === 0) {
            setChatMessages([]);
            return;
          }

          // Fetch messages from all channels in parallel
          const messagePromises = channels.map(async (channel) => {
            const messageCollection = new Collection(CollectionName.CHAT_MESSAGE, {
              createDocument: (src, opts) => new ChatMessage(src.id, opts),
              query: (ref) => ref
                .where('channelId', '==', channel.id)
                .where('isAiAugmented', '==', true)
                .orderBy('createdAt', 'desc'),
            });

            await messageCollection.fetch();

            return messageCollection.docs
              .map(processMessageDoc)
              .filter(Boolean);
          });

          const messageResults = await Promise.all(messagePromises);
          const allMessages = messageResults.flat();

          setChatMessages(allMessages);
          return;
        }

        // Only coach selected, no user -> load all coach's messages directly
        if (coachId && !userId) {
          // Fetch messages directly by userId (coachId) and isAiAugmented flag
          const messageCollection = new Collection(CollectionName.CHAT_MESSAGE, {
            createDocument: (src, opts) => new ChatMessage(src.id, opts),
            query: (ref) => ref
              .where('userId', '==', coachId)
              .where('isAiAugmented', '==', true)
              .orderBy('createdAt', 'desc'),
          });

          await messageCollection.fetch();
          const allMessageDocs = messageCollection.docs;

          if (allMessageDocs.length === 0) {
            setChatMessages([]);
            return;
          }

          // Group messages by channelId and limit to messagesPerUser per channel
          const messagesByChannel = new Map();
          allMessageDocs.forEach((doc) => {
            const channelId = doc.data?.channelId;
            if (!channelId) return;

            if (!messagesByChannel.has(channelId)) {
              messagesByChannel.set(channelId, []);
            }
            const channelMessages = messagesByChannel.get(channelId);
            if (channelMessages.length < (selectedMessagesPerUser || DEFAULT_MESSAGES_PER_USER_SELECTED)) {
              channelMessages.push(doc);
            }
          });

          // Process messages
          const allMessages = [];
          messagesByChannel.forEach((channelMessages) => {
            channelMessages.forEach((doc) => {
              const processedMessage = processMessageDoc(doc);
              if (processedMessage) {
                allMessages.push(processedMessage);
              }
            });
          });

          setChatMessages(allMessages);
          return;
        }

        // Both coach and user selected -> show augmented messages for that user
        if (userId && coachId) {
          const channel = await ChatChannel.getUserCoachChannel(userId, coachId);

          if (!channel) {
            setChatMessages([]);
            return;
          }

          const messageCollection = new Collection(CollectionName.CHAT_MESSAGE, {
            createDocument: (src, opts) => new ChatMessage(src.id, opts),
            query: (ref) => ref
              .where('channelId', '==', channel.id)
              .where('isAiAugmented', '==', true)
              .orderBy('createdAt', 'desc'),
          });

          await messageCollection.fetch();

          const messages = messageCollection.docs
            .map(processMessageDoc)
            .filter(Boolean);

          setChatMessages(messages);
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: 'SmartContextAnalytics',
            action: 'loadChatMessages',
          },
          extra: {
            userId,
            coachId,
          },
        });
        setChatMessages([]);
      } finally {
        setIsLoadingMessages(false);
      }
    };

    loadChatMessages();
  }, [userId, coachId, messageDiff, users, selectedMessagesPerUser, processMessageDoc]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: texts.columns.aiAugmentedText,
        headerName: texts.columns.aiAugmentedText,
        flex: 30,
        renderCell: ({ row }) => (
          <ScrollableCell>
            <ScrollableCellContent>{row[texts.columns.aiAugmentedText] || ''}</ScrollableCellContent>
          </ScrollableCell>
        ),
      },
      {
        field: texts.columns.difference,
        headerName: texts.columns.difference,
        flex: 30,
        renderCell: ({ row }) => (
          <ScrollableCell>
            <ScrollableCellContent>
              {row.diff !== null && row.diff !== undefined ? row.diff : (row[texts.columns.difference] || '')}
            </ScrollableCellContent>
          </ScrollableCell>
        ),
      },
      {
        field: texts.columns.sentMessages,
        headerName: texts.columns.sentMessages,
        flex: 30,
        renderCell: ({ row }) => (
          <ScrollableCell>
            <ScrollableCellContent>{row[texts.columns.sentMessages] || ''}</ScrollableCellContent>
          </ScrollableCell>
        ),
      },
      {
        field: texts.columns.percentChanged,
        headerName: texts.columns.percentChanged,
        flex: 10,
        renderCell: ({ row }) => (
          <ScrollableCell>
            <ScrollableCellContent>
              {row[texts.columns.percentChanged] || '0%'}
            </ScrollableCellContent>
          </ScrollableCell>
        ),
      },
      {
        field: texts.columns.date,
        headerName: texts.columns.date,
        flex: 10,
        renderCell: ({ row }) => (
          <ScrollableCell>
            <ScrollableCellContent>
              {row[texts.columns.date] || '-'}
            </ScrollableCellContent>
          </ScrollableCell>
        ),
      },
    ];

    return baseColumns;
  }, []);

  // Calculate average of percentChanged values from displayed messages
  const averagePercentChanged = useMemo(() => {
    if (chatMessages.length === 0) {
      return null;
    }

    const percentages = chatMessages
      .map((msg) => msg.percentChanged ?? 0)
      .filter((val) => typeof val === 'number');

    if (percentages.length === 0) {
      return null;
    }

    const sum = percentages.reduce((acc, val) => acc + val, 0);
    const average = sum / percentages.length;

    return Math.round(average * 100) / 100; // Round to 2 decimal places
  }, [chatMessages]);

  // Handle CSV download with summary row
  const handleCsvDownload = useCallback(async () => {
    setIsDownloadingCsv(true);
    try {
      let rowsToExport = chatMessages;

      if (averagePercentChanged !== null) {
        const summaryRow = {
          [texts.columns.aiAugmentedText]: '',
          [texts.columns.sentMessages]: texts.averagePercentChanged,
          [texts.columns.percentChanged]: `${averagePercentChanged}%`,
          [texts.columns.date]: '',
          [texts.columns.langfuseUrl]: '',
        };
        rowsToExport = [summaryRow, ...chatMessages];
      }

      const fileName = `${texts.csvFileNamePrefix} ${selectedUser?.label || selectedCoach?.label || 'All'}`;
      await downloadCsv(CSV_HEADERS, rowsToExport, fileName);
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [chatMessages, averagePercentChanged, selectedUser, selectedCoach]);

  return (
    <>
      <StyledHeader>
        <Title>{texts.title}</Title>
        <OptionContainer>
          <CoachSelect onChange={onCoachSelect} />
          <UserSelect
            selectedUser={selectedUser}
            userList={users}
            onChange={onUserSelect}
          />
          {!selectedUser && (
            <SelectWrapper>
              <Select
                value={selectedMessagesPerUser || DEFAULT_MESSAGES_PER_USER_SELECTED}
                options={MESSAGES_PER_USER_OPTIONS}
                onChange={(e) => setSelectedMessagesPerUser(Number(e.target.value))}
                label={texts.messagesPerUser}
              />
            </SelectWrapper>
          )}
        </OptionContainer>
      </StyledHeader>
      {averagePercentChanged !== null && (
        <MedianContainer>
          <MedianInfoWrapper>
            <MedianLabel>
              {texts.averagePercentChanged}
              :
            </MedianLabel>
            <MedianValue>
              {averagePercentChanged}
              %
            </MedianValue>
          </MedianInfoWrapper>
          <DownloadCSVButton
            onClick={handleCsvDownload}
            disabled={chatMessages.length === 0}
          />
        </MedianContainer>
      )}
      <LoadingOverlay isLoading={isLoading || isLoadingMessages || isDownloadingCsv} />
      <DataGridContainer>
        <GenericDataGrid
          rows={chatMessages}
          columns={columns}
          disableSelectionOnClick
          rowHeight={DEFAULT_ROW_HEIGHT}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
        />
      </DataGridContainer>
    </>
  );
};

export default SmartContextAnalytics;
